import { useContext, useEffect, useState } from 'react'
import { ConfigProvider, Dropdown, Layout, Watermark } from 'antd'
import { DateTime } from 'luxon'
import { Link, Outlet } from 'react-router-dom'
import { Bell, Gear, List, UserCircle } from '@phosphor-icons/react'
import { PersonCircleOutline } from 'react-ionicons'

import logo from '../../../assets/small-logo.svg'

import { SettingsModal } from '../../../screens/settingsModal/SettingsModal'
import { DropdownMenu } from '../menus/DropdownMenu'
import { InternalLayoutLoader } from '../InternalLayoutLoader'
import { Player } from '../../../components/sound/Player'

import { AuthContext } from '../../../context/AuthContext/AuthContext'
import { GeneralContext } from '../../../context/GeneralContext/GeneralContext'
import { displayMessage } from '../../../helpers/messages'
import { colors } from '../../../services/style'
import { SirenContext } from '../../../context/SirenContext/SirenContext'

export const InternalLayout = () => {
    const { setIsPlaying, setPlaySiren } = useContext(SirenContext)
    const { onChangeIsLogged, currentUser } = useContext(AuthContext)
    const { windowHeight, windowWidth, getUserTotalizers, generalUserData } = useContext(GeneralContext)

    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false)

    const handleOnClickLogout = async () => {
        setIsPlaying(false)
        setPlaySiren(false)
        await onChangeIsLogged(false)
    }

    const getFormattedDateTime = () => {
        const daysOfWeek = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']
        const weekDay = daysOfWeek[DateTime.now().toFormat('c')]
        const date = DateTime.now().setLocale('pt-BR').toFormat("dd 'de' LLLL ' - ' T")
        return `${weekDay} ${date}`
    }
    const getFormattedAlertTime = () => {
        if (!generalUserData?.last_alert) return displayMessage('EMPTY')
        const last_alert_date = DateTime.fromISO(generalUserData?.last_alert)
        const now = DateTime.now()

        const diff = now.diff(last_alert_date, ['hours', 'minutes'])

        const { hours, minutes } = diff
        return `${parseInt(hours)}' ${parseInt(minutes)}''`
    }

    const [currentDateTime, setCurrentDateTime] = useState(getFormattedDateTime())
    const [currentLastAlertTime, setCurrentLastAlertTime] = useState()

    const items = [
        {
            key: '/logout',
            label: (
                <Link to={'/login'} onClick={handleOnClickLogout} className="flex nav-bar-menu-option-gap items-center">
                    <span className="ml-1 font-medium navbar-user-name">{displayMessage('LOGOUT')}</span>
                </Link>
            ),
            style: { width: '100%' },
        },
    ]

    useEffect(() => {
        getUserTotalizers()
        const timer = setInterval(() => {
            setCurrentDateTime(getFormattedDateTime())
            if (generalUserData?.last_alert) setCurrentLastAlertTime(getFormattedAlertTime())
        }, 1000)

        return () => clearInterval(timer)
    }, [])

    useEffect(() => {
        if (generalUserData) setCurrentLastAlertTime(getFormattedAlertTime())
    }, [generalUserData])

    return (
        <div className="bg-sl-gray-1000 h-screen w-full monitor">
            {isSettingsModalOpen && <SettingsModal onClose={() => setIsSettingsModalOpen(false)} />}
            <Watermark
                zIndex={100}
                rotate={0}
                color={colors['sl-gray'][150]}
                image={logo}
                height={windowHeight}
                width={windowHeight}
                offset={[windowWidth - windowHeight, 0]}
                className=" h-screen w-full"
            >
                <Layout className="bg-sl-gray-900 h-full w-full text-white">
                    <InternalLayoutLoader />
                    <div className="flex flex-row justify-between items-center w-full bg-sl-gray-1000 py-4 px-6 navbar">
                        <div className="navbar--content">
                            <DropdownMenu />
                            {/* <div className="flex flex-col gap-2">
                                <span className="opacity-40 navbar--title" uppercase">{displayMessage('CLIENT')}</span>
                                <span className="uppercase navbar--subtitle">{generalUserData?.client ?? displayMessage('EMPTY')}</span>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className="opacity-40 navbar--title" uppercase">{displayMessage('LOCATION_MENU_TITLE')}</span>
                                <span className="uppercase navbar--subtitle">{generalUserData?.location ?? displayMessage('EMPTY')}</span>
                            </div> */}
                            <div className="flex flex-col">
                                <span className="opacity-40 uppercase navbar--title">{displayMessage('CENTRALS')}</span>
                                <span className="uppercase navbar--subtitle">{generalUserData?.centrals ?? displayMessage('EMPTY')}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="opacity-40 uppercase navbar--title">{displayMessage('DEVICES')}</span>
                                <span className="uppercase navbar--subtitle">{generalUserData?.devices ?? displayMessage('EMPTY')}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="opacity-40 uppercase navbar--title">{displayMessage('TIME_FROM_LAST_ALERT')}</span>
                                <span className="uppercase navbar--subtitle">{currentLastAlertTime ?? displayMessage('EMPTY')}</span>
                            </div>
                        </div>
                        <div className="flex flex-row items-center navbar--actions mr-1 navbar-icons">
                            <span className="h-full opacity-40 navbar--date uppercase">{currentDateTime}</span>
                            <ConfigProvider
                                theme={{
                                    token: {
                                        colorBgBase: colors['sl-gray'][900],
                                        colorText: colors['sl-gray'][50],
                                        borderRadius: '12px',
                                        lineHeight: '12px',
                                    },
                                }}
                            >
                                <Dropdown className="bg-sl-gray-100" menu={{ items }} trigger={['click']}>
                                    <div
                                        className="flex flex-row justify-center bg-sl-gray-100 navbar--user rounded-full items-center cursor-pointer"
                                        style={{ border: `1px solid ${colors['sl-gray'][100]}` }}
                                    >
                                        <UserCircle color={'#ffffff'} className="navbar--user--icon" />
                                        <p className=" text-white font-medium navbar--user--name">{currentUser?.first_name}</p>
                                    </div>
                                </Dropdown>
                            </ConfigProvider>
                            <Player />
                            <div className="bg-sl-gray-100 rounded-full flex justify-center items-center navbar--button cursor-pointer" style={{ border: `1px solid ${colors['sl-gray'][100]}` }}>
                                <Bell weight="fill" color={'#ffffff'} className="navbar--icon" />
                            </div>
                            <div
                                className="bg-sl-gray-100 rounded-full flex justify-center items-center navbar--button cursor-pointer"
                                onClick={() => setIsSettingsModalOpen(true)}
                                style={{ border: `1px solid ${colors['sl-gray'][100]}` }}
                            >
                                <Gear weight="fill" color={'#ffffff'} className="navbar--icon" />
                            </div>
                        </div>
                    </div>

                    <div className={`flex flex-col flex-1 p-2 smb:p-4 smb:w-unset overflow-auto h-full w-full`}>
                        <Outlet />
                    </div>
                </Layout>
            </Watermark>
        </div>
    )
}
